<template>
    <div class="clinic">
        <div class="steps-header">
            <button
                v-for="(, index) in clinic.questions"
                :key="index"
                class="step"
                :aria-label="$t('clinic.step',{ index: index + 1, steps: clinic.questions.length })"
                :disabled="!clinic.getStepStatus(index).answered"
                @click="clinic.setStep(index)"
            >
                <div
                    v-if="clinic.getStepStatus(index).answered"
                    class="step-indicator done"
                >
                    <DSIcon code="default-line/check" size="l" aria-hidden="true" />
                </div>
                <div
                    v-else-if="clinic.getStepStatus(index)"
                    class="step-indicator"
                    :class="{active: clinic.getStepStatus(index).active}"
                >
                    {{ index+1 }}
                </div>
                <div
                    class="step-underline"
                    :class="{ active: index === clinic.step }"
                />
            </button>
        </div>
        <div v-for="(question, index) in clinic.questions" :key="index" class="steps-main">
            <div v-if="clinic.step === index" class="question">
                <h2 class="h5">{{ index+1 }}. {{ question.question }}</h2>
                <div class="answers">
                    <NuxtLink
                        v-for="(answer, i) in question.answers"
                        :key="i"
                        class="ds-btn ds-btn--secondary"
                        :class="{'ds-btn--primary': answer.selected}"
                        @click="nextStep(index, i, answer.points)"
                    >
                        {{ answer.answer }}
                    </NuxtLink>
                </div>
            </div>
        </div>
        <div class="steps-footer">
            <NuxtLink v-if="clinic.step > 0" class="ds-btn ds-btn-large ds-btn--secondary prev" @click="clinic.prevStep()">
                <DSIcon code="default-line/chevron-left" size="l" />
                {{ $t('clinic.prev') }}
            </NuxtLink>
            <NuxtLink
                v-if="clinic.getStepStatus(clinic.step).answered && clinic.step < lastStep-1"
                class="ds-btn ds-btn-large ds-btn--primary next"
                @click="clinic.nextStep()"
            >
                {{ $t('clinic.next') }}
                <DSIcon code="default-line/chevron-right" size="l" />
            </NuxtLink>
            <NuxtLink v-else-if="clinic.isCompleted()" class="ds-btn ds-btn-large ds-btn--primary reset" @click="clinic.reset()">
                {{ $t('clinic.reset') }}
            </NuxtLink>
        </div>
    </div>
</template>
<script setup>
import { useI18n } from '#imports';
import { ref, computed } from 'vue';
import { useCarwashClinicStore } from '~dsc/stores/cms/carwashclinic';
const clinic = useCarwashClinicStore();

const stepColumns = ref(`repeat(${clinic.questions.length}, 1fr)`);
const answerColumns = computed(() => `repeat(${clinic.questions[clinic.step].answers.length}, 1fr)`);

const lastStep = clinic.questions.length;

const emit = defineEmits([
    'completed',
]);

const nextStep = (index, i, points) => {
    clinic.toggleSelect(index, i, points);
    if (clinic.isCompleted()) emit('completed');
};

useI18n({
    useScope: 'global',
    messages: {
        'fi-FI': {
            clinic: {
                next: 'Seuraava',
                prev: 'Edellinen',
                reset: 'Tee uudelleen',
                step: 'Olet kyselyn "Kuinka likainen autosi on?" kohdassa {index} / {steps}',
            },
        },
        'en-EU': {
            clinic: {
                next: 'Next',
                prev: 'Previous',
                reset: 'Reset',
                step: 'You are on the step {index} / {steps} of the Carwash survey',
            },
        },
    },
});

</script>

<style scoped>
.inner h1 {
  text-align: center;
  max-width: calc(100dvw - 4rem);
}
.inner .clinic {
  border-radius: 0.25rem;
  border: 1px solid var(--color-base-grey-200);
  max-width: calc(100dvw - 4rem);
}
.inner .clinic .steps-header {
  display: grid;
  grid-template-columns: v-bind(stepColumns);
  grid-template-rows: 1fr auto;
}
.inner .clinic .steps-header .step {
  display: grid;
  padding: 1rem 0;
  width: 100%;
  cursor: pointer;
}
.inner .clinic .steps-header .step .step-indicator {
  display: grid;
  background-color: var(--color-base-grey-100);
  height: 2rem;
  width: 2rem;
  justify-self: center;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  color: var(--color-base-grey-600);
  font-weight: 900;
}
.inner .clinic .steps-header .step .step-indicator.active {
  background-color: var(--color-primary-500);
  color: var(--color-base-black-500);
}
.inner .clinic .steps-header .step .step-indicator.done {
  background-color: var(--color-primary-500);
  color: var(--color-base-black-500);
}
.inner .clinic .steps-header .step .step-underline {
  margin-top: 0.5rem;
  width: 0;
  height: 0.25rem;
  background-color: var(--color-primary-500);
  transition: width 0.3s ease-in-out;
}
.inner .clinic .steps-header .step .step-underline.active {
  width: 100%;
}
.inner .clinic .steps-header .step:hover .step-underline {
  width: 100%;
}
.inner .clinic .steps-main {
  padding: 0 2rem;
}
.inner .clinic .steps-main .question .answers {
  display: grid;
  grid-template-columns: v-bind(answerColumns);
  gap: 1rem;
}
@media (max-width: 1199px) {
  .inner .clinic .steps-main .question .answers {
    grid-template-columns: 1fr;
  }
}
.inner .clinic .steps-main .question .answers * {
  align-items: center;
  height: 5rem;
}
.inner .clinic .steps-footer {
  padding: 2rem;
  display: grid;
  grid-template-columns: auto 1fr auto;
}
.inner .clinic .steps-footer .ds-btn {
  display: grid;
  align-items: center;
  grid-template-columns: 1fr auto;
  text-transform: uppercase;
  font-weight: 500;
  height: 3.25rem;
}
.inner .clinic .steps-footer .next,
.inner .clinic .steps-footer .reset {
  grid-column-start: 3;
}
</style>
